import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import CircularProgress from '@mui/material/CircularProgress';
import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AttendanceEmployeeFilter,
  AttendanceEmployeeRow,
  AttendanceEmployeeSummary
} from 'services/AttendanceTrackingService';
import {
  formatEmployeeLoadedCount,
  formatLeaveCount,
  formatOfficeDays,
  formatWorkingHours,
  getComplianceClass,
  getComplianceLabel,
  getWorkingHoursTone
} from 'utils/formatAttendanceEmployee';
import { AttendanceDashboardPeriod } from 'utils/attendanceDashboardPeriod';
import { DEFAULT_FULL_DAY_HOURS, DEFAULT_SHORT_DAY_HOURS } from 'utils/formatAttendanceDashboard';
import { buildManagerAttendanceDetailPath } from 'utils/managerPortalPaths';
import '../../../organisms/AttendanceEmployeePanel/index.scss';

const FILTER_OPTIONS: { id: AttendanceEmployeeFilter; label: string }[] = [
  { id: 'all', label: 'All' },
  { id: 'on-leave', label: 'On Leave' },
  { id: 'below', label: 'Below' },
  { id: 'missing', label: 'Missing' }
];

interface Props {
  summary: AttendanceEmployeeSummary | null;
  items: AttendanceEmployeeRow[];
  totalCount: number;
  loading: boolean;
  loadingMore: boolean;
  exportingScope: 'all' | 'selected' | null;
  selectingAll: boolean;
  hasMore: boolean;
  period: AttendanceDashboardPeriod;
  onLoadMore: () => void;
  onExport: (scope: 'all' | 'selected') => void;
  search: string;
  filter: AttendanceEmployeeFilter;
  selectedIds: Set<string>;
  onSearchChange: (value: string) => void;
  onFilterChange: (filter: AttendanceEmployeeFilter) => void;
  onToggleRow: (employeeId: string) => void;
  onToggleAll: (checked: boolean) => void;
  onClearSelection: () => void;
}

const LOAD_MORE_THRESHOLD_PX = 120;

export const ManagerAttendancePanel = ({
  summary,
  items,
  totalCount,
  loading,
  loadingMore,
  exportingScope,
  selectingAll,
  hasMore,
  period,
  onLoadMore,
  onExport,
  search,
  filter,
  selectedIds,
  onSearchChange,
  onFilterChange,
  onToggleRow,
  onToggleAll,
  onClearSelection
}: Props) => {
  const navigate = useNavigate();
  const headerCheckboxRef = useRef<HTMLInputElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const loadMoreRef = useRef<HTMLTableRowElement>(null);
  const onLoadMoreRef = useRef(onLoadMore);
  const isFullMonth = summary?.isFullCalendarMonth ?? false;

  onLoadMoreRef.current = onLoadMore;

  const allVisibleSelected = useMemo(
    () => items.length > 0 && items.every((i) => selectedIds.has(i.employeeId)),
    [items, selectedIds]
  );

  const shouldLoadMore = useCallback(() => {
    if (!hasMore || loading || loadingMore) return false;

    const root = scrollRef.current;
    if (!root) return false;

    const distanceFromBottom =
      root.scrollHeight - root.scrollTop - root.clientHeight;

    return distanceFromBottom <= LOAD_MORE_THRESHOLD_PX;
  }, [hasMore, loading, loadingMore]);

  const tryLoadMore = useCallback(() => {
    if (shouldLoadMore()) {
      onLoadMoreRef.current();
    }
  }, [shouldLoadMore]);

  const fillScrollContainerIfNeeded = useCallback(() => {
    const root = scrollRef.current;
    if (!root || !hasMore || loading || loadingMore) return;

    if (root.scrollHeight <= root.clientHeight + 4) {
      onLoadMoreRef.current();
    }
  }, [hasMore, loading, loadingMore]);

  useEffect(() => {
    if (!headerCheckboxRef.current) return;
    headerCheckboxRef.current.indeterminate =
      selectedIds.size > 0 && selectedIds.size < totalCount;
  }, [selectedIds, totalCount]);

  useEffect(() => {
    const root = scrollRef.current;
    const target = loadMoreRef.current;

    if (!root || !target || !hasMore || loading || loadingMore) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          onLoadMoreRef.current();
        }
      },
      {
        root,
        rootMargin: `${LOAD_MORE_THRESHOLD_PX}px`,
        threshold: 0
      }
    );

    observer.observe(target);

    return () => observer.disconnect();
  }, [hasMore, loading, loadingMore, items.length]);

  useEffect(() => {
    if (loading) return;

    let cancelled = false;

    const runLayoutChecks = () => {
      if (cancelled) return;
      tryLoadMore();
      fillScrollContainerIfNeeded();
    };

    const frame = requestAnimationFrame(() => {
      requestAnimationFrame(runLayoutChecks);
    });

    const resizeObserver =
      typeof ResizeObserver !== 'undefined' && scrollRef.current
        ? new ResizeObserver(runLayoutChecks)
        : null;

    if (resizeObserver && scrollRef.current) {
      resizeObserver.observe(scrollRef.current);
    }

    const layoutTimer = window.setTimeout(runLayoutChecks, 300);

    return () => {
      cancelled = true;
      cancelAnimationFrame(frame);
      clearTimeout(layoutTimer);
      resizeObserver?.disconnect();
    };
  }, [
    loading,
    loadingMore,
    hasMore,
    items.length,
    tryLoadMore,
    fillScrollContainerIfNeeded
  ]);

  const handleTableScroll = useCallback(() => {
    tryLoadMore();
  }, [tryLoadMore]);

  const handleExport = (scope: 'all' | 'selected') => {
    onExport(scope);
  };

  const renderComplianceStatus = (row: AttendanceEmployeeRow) => {
    const statusClass = getComplianceClass(row.complianceStatus, isFullMonth);

    return (
      <span
        className={`attendance-employee__status-text attendance-employee__status-text--${statusClass}`}
      >
        {getComplianceLabel(row.complianceStatus, isFullMonth)}
      </span>
    );
  };

  const exportingAll = exportingScope === 'all';
  const exportingSelected = exportingScope === 'selected';

  return (
    <div className="attendance-employee">
      <div className="attendance-employee__toolbar">
        <label className="attendance-employee__search">
          <SearchOutlinedIcon fontSize="small" aria-hidden />
          <input
            type="search"
            value={search}
            placeholder="Search name or ID..."
            onChange={(event) => onSearchChange(event.target.value)}
          />
        </label>

        <div
          className="attendance-employee__filters"
          role="group"
          aria-label="Filters"
        >
          {FILTER_OPTIONS.map((option) => {
            const disabled = option.id === 'below' && !isFullMonth;

            return (
              <button
                key={option.id}
                type="button"
                className={`attendance-employee__filter${
                  filter === option.id
                    ? ' attendance-employee__filter--active'
                    : ''
                }`}
                disabled={disabled}
                onClick={() => onFilterChange(option.id)}
              >
                {option.label}
              </button>
            );
          })}
        </div>

        <div className="attendance-employee__toolbar-end">
          <button
            type="button"
            className="attendance-employee__export-btn"
            disabled={exportingScope !== null || loading}
            onClick={() => handleExport('all')}
          >
            {exportingAll ? (
              <CircularProgress size={16} color="inherit" aria-hidden />
            ) : (
              <DownloadOutlinedIcon fontSize="small" aria-hidden />
            )}
            <span>{exportingAll ? 'Preparing Excel...' : 'Export Excel'}</span>
          </button>
        </div>
      </div>

      {selectedIds.size > 0 && (
        <div className="attendance-employee__selection-bar">
          <span className="attendance-employee__selection-count">
            <GroupsOutlinedIcon fontSize="small" aria-hidden />
            {selectedIds.size} employee{selectedIds.size === 1 ? '' : 's'}{' '}
            selected
          </span>
          <div className="attendance-employee__selection-actions">
            <button
              type="button"
              className="attendance-employee__selection-btn attendance-employee__selection-btn--primary"
              disabled={exportingScope !== null}
              onClick={() => handleExport('selected')}
            >
              {exportingSelected ? (
                <>
                  <CircularProgress size={14} color="inherit" aria-hidden />
                  Preparing Excel...
                </>
              ) : (
                <>
                  <DownloadOutlinedIcon fontSize="small" aria-hidden />
                  Export Selected
                </>
              )}
            </button>
            <button
              type="button"
              className="attendance-employee__selection-btn attendance-employee__selection-btn--ghost"
              onClick={onClearSelection}
            >
              Clear Selection
            </button>
          </div>
        </div>
      )}

      <section className="attendance-employee__card">
        <div
          className="attendance-employee__table-wrap"
          ref={scrollRef}
          aria-busy={loadingMore}
          onScroll={handleTableScroll}
        >
          {loading ? (
            <div className="attendance-employee__table-loading">
              <CircularProgress size={28} />
              <span>Loading employee attendance...</span>
            </div>
          ) : items.length > 0 ? (
            <table className="attendance-employee__table">
              <thead>
                <tr>
                  <th className="attendance-employee__th-check">
                    <input
                      ref={headerCheckboxRef}
                      type="checkbox"
                      aria-label={`Select all ${totalCount} employees`}
                      checked={allVisibleSelected}
                      disabled={selectingAll || loading || totalCount === 0}
                      onChange={(event) => onToggleAll(event.target.checked)}
                    />
                  </th>
                  <th>ID</th>
                  <th>Name</th>
                  <th>Office Days</th>
                  <th>Full Leave</th>
                  <th>Half Leave</th>
                  <th>Working Hrs</th>
                  <th>Compliance Status</th>
                </tr>
              </thead>
              <tbody>
                {items.map((row) => (
                  <tr
                    key={row.employeeId}
                    className="attendance-employee__row"
                    onClick={() =>
                      navigate(
                        buildManagerAttendanceDetailPath(row.employeeId, period)
                      )
                    }
                  >
                    <td className="attendance-employee__td-check">
                      <input
                        type="checkbox"
                        aria-label={`Select ${row.employeeName}`}
                        checked={selectedIds.has(row.employeeId)}
                        onClick={(event) => event.stopPropagation()}
                        onChange={() => onToggleRow(row.employeeId)}
                      />
                    </td>
                    <td>{row.employeeId}</td>
                    <td>
                      <button
                        type="button"
                        className="attendance-employee__name-link"
                        onClick={(event) => {
                          event.stopPropagation();
                          navigate(
                            buildManagerAttendanceDetailPath(
                              row.employeeId,
                              period
                            )
                          );
                        }}
                      >
                        {row.employeeName}
                      </button>
                    </td>
                    <td>
                      {formatOfficeDays(
                        row.presentDays,
                        row.workingDaysInPeriod
                      )}
                    </td>
                    <td>{formatLeaveCount(row.fullDayLeaveCount)}</td>
                    <td>{formatLeaveCount(row.halfDayLeaveCount)}</td>
                    <td>
                      <span
                        className={`attendance-employee__hours attendance-employee__hours--${getWorkingHoursTone(
                          row.totalHours,
                          row.presentDays,
                          summary?.fullDayHoursThreshold ??
                            DEFAULT_FULL_DAY_HOURS,
                          summary?.shortDayHoursThreshold ??
                            DEFAULT_SHORT_DAY_HOURS
                        )}`}
                      >
                        {formatWorkingHours(row.totalHours)}
                      </span>
                    </td>
                    <td>{renderComplianceStatus(row)}</td>
                  </tr>
                ))}
                {hasMore && (
                  <tr
                    ref={loadMoreRef}
                    className="attendance-employee__load-more-row"
                  >
                    <td colSpan={8}>
                      <span className="attendance-employee__load-more">
                        {loadingMore ? (
                          <>
                            <CircularProgress size={18} />
                            Loading more employees...
                          </>
                        ) : (
                          'Scroll for more'
                        )}
                      </span>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          ) : (
            <div className="attendance-employee__empty">
              No employees match your filters.
            </div>
          )}
        </div>

        <footer className="attendance-employee__footer">
          <span className="attendance-employee__footer-meta">
            {formatEmployeeLoadedCount(items.length, totalCount)}
          </span>
        </footer>
      </section>
    </div>
  );
};
